import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

interface ListPageTitleProps {
  /** Icon node — typically a 16px lucide icon. The wrapping square uses
   *  the secondary token (light brand bg + dark brand fg). */
  icon: ReactNode;
  /** The page title text. Rendered inside an `<h1>`. */
  children: ReactNode;
  /** Optional subtitle line rendered below the title row. */
  subtitle?: ReactNode;
  /** Optional brand node that REPLACES the icon square — e.g. the logo of the
   *  company/workspace the page is scoped to. Consumers pass a self-contained
   *  avatar (logo image or initials fallback). When present, the generic `icon`
   *  is not rendered. */
  brand?: ReactNode;
}

/**
 * Standard title block for list landing pages: square brand-tinted icon (or the
 * workspace `brand` logo when provided) + h1 + optional subtitle. Every list
 * page (`Empresas`, `Programas`, etc.) uses this so the header rhythm stays
 * identical across the surface.
 */
export function ListPageTitle({ icon, children, subtitle, brand }: ListPageTitleProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row items-center gap-3">
        {brand ?? (
          <div
            aria-hidden="true"
            className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground"
          >
            {icon}
          </div>
        )}
        <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
          {children}
        </h1>
      </div>
      {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
    </div>
  );
}

interface TableSectionProps {
  /** The `<Table>` element. Receives `containerClassName` so its scroll
   *  container fills the section. */
  children: ReactNode;
  /** Optional pagination row pinned to the bottom of the section. */
  pagination?: ReactNode;
  /** Accessible label propagated to the wrapping section. */
  ariaLabel: string;
  /** Mirrors the `aria-busy` of the underlying list query. */
  ariaBusy?: boolean;
  className?: string;
  /** Test id for e2e selectors. */
  'data-testid'?: string;
}

/**
 * Wraps a `<Table>` (or a placeholder of equivalent shape) in a card-styled
 * section that fills the remaining vertical space inside the list body.
 * The table itself scrolls internally; the optional `pagination` row is pinned
 * at the bottom.
 *
 * The consumer is responsible for telling the `<Table>` to fill the container —
 * the cleanest way is to pass `containerClassName="flex-1 overflow-auto"` so
 * `<Table>`'s scroll container becomes the flex item that grows.
 */
export function ListTableSection({
  children,
  pagination,
  ariaLabel,
  ariaBusy,
  className,
  ...rest
}: TableSectionProps) {
  return (
    <section
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
      data-testid={rest['data-testid']}
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]',
        className
      )}
    >
      {children}
      {pagination}
    </section>
  );
}

interface ListPlaceholderProps {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
  role?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
}

/**
 * Drop-in container for empty/loading/error states that should fill the
 * remaining vertical space (instead of collapsing to their content height).
 * Renders the same dashed-border card shell as the table section so the
 * surface stays visually consistent across all states.
 */
export function ListPlaceholder({ children, className, ...rest }: ListPlaceholderProps) {
  return (
    <div
      data-testid={rest['data-testid']}
      role={rest.role}
      aria-live={rest['aria-live']}
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-5 rounded-lg border border-dashed border-border bg-card p-8 text-center',
        className
      )}
    >
      {children}
    </div>
  );
}

interface ListHeaderProps {
  /** Top-left: title block — typically `<ListPageTitle>`. */
  title: ReactNode;
  /** Top-right: primary actions (Create button, etc.). */
  actions?: ReactNode;
  /** Bottom-left: filter dropdowns / popovers. Reserved slot — when only
   *  `tools` is provided, an empty placeholder keeps the right column anchored. */
  filters?: ReactNode;
  /** Bottom-right: tools — search field, sort, view toggle. */
  tools?: ReactNode;
}

/**
 * Standard two-row header for list pages — title + primary CTA
 * on the top row, filters (left) + tools (right) on the bottom row.
 *
 * Each slot is optional; the second row collapses entirely when both
 * `filters` and `tools` are absent.
 */
export function ListHeader({ title, actions, filters, tools }: ListHeaderProps) {
  const hasSecondRow = filters !== undefined || tools !== undefined;

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {title}
        {actions ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">{actions}</div>
        ) : null}
      </div>

      {hasSecondRow ? (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Empty placeholder keeps `lg:justify-between` anchoring `tools`
              to the right when there are no filters. */}
          {filters ? (
            <div className="flex flex-row flex-wrap items-center gap-2">{filters}</div>
          ) : (
            <div aria-hidden />
          )}
          {tools ? (
            <div className="flex flex-row items-center gap-2 lg:justify-end">{tools}</div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
